/**
 * Cache store slice for API response caching and data management
 * Provides intelligent caching with TTL support for API responses
 */

import { useEffect } from 'react'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { devtoolsDev } from '../middleware'
import type { CacheEntry, CacheStore } from '../types'

/**
 * Default TTL for cache entries (5 minutes)
 */
const DEFAULT_TTL = 5 * 60 * 1000

/**
 * Cache cleanup interval (1 minute)
 */
const CLEANUP_INTERVAL = 60 * 1000

/**
 * Initial cache state
 */
const initialCacheState = {
  cache: {},
  defaultTTL: DEFAULT_TTL
}

/**
 * Creates the cache store with actions
 */
const createCacheStore = () => create<CacheStore>()(
  devtoolsDev(
    // @ts-ignore
    immer((set, get) => ({
      // Initial state
      ...initialCacheState,

      // Cache actions
      setCache: <T>(key: string, data: T, ttl?: number) => {
        const expiryTime = ttl || get().defaultTTL
        const entry: CacheEntry<T> = {
          data,
          timestamp: Date.now(),
          ttl: expiryTime,
          key
        }

        set((state) => {
          state.cache[key] = entry
        })
      },

      getCache: <T>(key: string): T | null => {
        const state = get()
        const entry = state.cache[key] as CacheEntry<T> | undefined
        
        if (!entry) {
          return null
        }

        // Check if cache entry has expired
        const isExpired = Date.now() - entry.timestamp > entry.ttl
        if (isExpired) {
          // Remove expired entry
          set((state) => {
            delete state.cache[key]
          })
          return null
        }

        return entry.data
      },

      clearCache: (key?: string) => {
        set((state) => {
          if (key) {
            delete state.cache[key]
          } else {
            state.cache = {}
          }
        })
      },

      cleanExpiredCache: () => {
        const now = Date.now()
        set((state) => {
          Object.keys(state.cache).forEach(key => {
            const entry = state.cache[key]
            if (now - entry.timestamp > entry.ttl) {
              delete state.cache[key]
            }
          })
        })
      },

      isCacheValid: (key: string): boolean => {
        const state = get()
        const entry = state.cache[key]
        
        if (!entry) {
          return false
        }

        return Date.now() - entry.timestamp <= entry.ttl
      }
    })),
    'CacheStore'
  )
)

/**
 * Cache store instance
 */
export const cacheStore = createCacheStore()

/**
 * Cache store hook for components
 * @returns Cache store state and actions
 */
export const useCacheStore = () => cacheStore()

/**
 * Hook for cache management
 * @returns Cache actions and utilities
 */
export const useCache = () => {
  const { setCache, getCache, clearCache, cleanExpiredCache, isCacheValid } = useCacheStore()
  
  return {
    setCache,
    getCache,
    clearCache,
    cleanExpiredCache,
    isCacheValid,
    // Convenience methods
    setCacheWithDefaultTTL: <T>(key: string, data: T) => setCache(key, data),
    setCacheWithCustomTTL: <T>(key: string, data: T, minutes: number) => 
      setCache(key, data, minutes * 60 * 1000),
    clearAllCache: () => clearCache(),
    clearCacheByPattern: (pattern: string) => {
      const state = cacheStore.getState()
      const keysToDelete = Object.keys(state.cache).filter(key => 
        key.includes(pattern) || key.match(new RegExp(pattern))
      )
      keysToDelete.forEach(key => clearCache(key))
    }
  }
}

/**
 * Hook for API cache management with HTTP endpoints
 * @returns API-specific cache utilities
 */
export const useApiCache = () => {
  const { setCache, getCache, clearCache, isCacheValid } = useCache()
  
  const generateCacheKey = (url: string, params?: Record<string, any>) => {
    const paramString = params ? JSON.stringify(params) : ''
    return `api:${url}${paramString ? `:${paramString}` : ''}`
  }
  
  return {
    // Cache API response
    cacheApiResponse: <T>(url: string, data: T, params?: Record<string, any>, ttl?: number) => {
      const key = generateCacheKey(url, params)
      setCache(key, data, ttl)
    },
    
    // Get cached API response
    getCachedApiResponse: <T>(url: string, params?: Record<string, any>): T | null => {
      const key = generateCacheKey(url, params)
      return getCache<T>(key)
    },
    
    // Check if API response is cached and valid
    isApiResponseCached: (url: string, params?: Record<string, any>): boolean => {
      const key = generateCacheKey(url, params)
      return isCacheValid(key)
    },
    
    // Clear specific API cache
    clearApiCache: (url: string, params?: Record<string, any>) => {
      const key = generateCacheKey(url, params)
      clearCache(key)
    },
    
    // Clear all API cache
    clearAllApiCache: () => {
      const state = cacheStore.getState()
      const apiKeys = Object.keys(state.cache).filter(key => key.startsWith('api:'))
      apiKeys.forEach(key => clearCache(key))
    }
  }
}

/**
 * Hook for cache statistics
 * @returns Cache statistics and information
 */
export const useCacheStats = () => {
  const cache = cacheStore((state) => state.cache)
  
  const stats = {
    totalEntries: Object.keys(cache).length,
    expiredEntries: 0,
    validEntries: 0,
    totalSize: 0,
    oldestEntry: null as CacheEntry | null,
    newestEntry: null as CacheEntry | null
  }
  
  const now = Date.now()
  const entries = Object.values(cache)
  
  entries.forEach(entry => {
    const isExpired = now - entry.timestamp > entry.ttl
    if (isExpired) {
      stats.expiredEntries++
    } else {
      stats.validEntries++
    }
    
    // Rough size calculation (in bytes)
    stats.totalSize += JSON.stringify(entry).length
    
    if (!stats.oldestEntry || entry.timestamp < stats.oldestEntry.timestamp) {
      stats.oldestEntry = entry
    }
    
    if (!stats.newestEntry || entry.timestamp > stats.newestEntry.timestamp) {
      stats.newestEntry = entry
    }
  })
  
  return stats
}

/**
 * Custom hook for automatic cache cleanup
 * Sets up interval to clean expired cache entries
 */
export const useCacheCleanup = (interval: number = CLEANUP_INTERVAL) => {
  const { cleanExpiredCache } = useCache()
  
  // Setup cleanup interval
  useEffect(() => {
    const intervalId = setInterval(cleanExpiredCache, interval)
    
    return () => clearInterval(intervalId)
  }, [cleanExpiredCache, interval])
}

/**
 * Utility function to create cache keys
 * @param prefix - Cache key prefix
 * @param identifier - Unique identifier
 * @param params - Optional parameters
 * @returns Formatted cache key
 */
export const createCacheKey = (
  prefix: string, 
  identifier: string, 
  params?: Record<string, any>
): string => {
  const paramString = params ? JSON.stringify(params) : ''
  return `${prefix}:${identifier}${paramString ? `:${paramString}` : ''}`
}
